import { ArtifactProto, ExecutionStepProto, ResponseMessageProto } from "build-ts/chat_session";
import { ExecutionStatusType } from "build-ts/common";
import { KGDataFrameProto } from "build-ts/dataframe";
import type { ToolExecutionRun } from "../utils/executionStepRun";

export interface ChatMessageRow {
  message_id: string;
  session_id: string;
  session_title: string;
  session_created_at: string;
  user_id: string;
  message_timestamp: string;
  analyst_id: string;
  num_raw_dataframes: number;
  num_workflows: number;
  num_workflow_outputs: number;
  num_code_snippets: number;
  num_pandas_dataframes: number;
  num_charts: number;
  num_reports: number;
  num_file_artifacts: number;
  num_workflow_runs: number;
  num_workflow_run_searches: number;
}

export interface ChatMessagesResponse {
  rows: ChatMessageRow[];
  total: number;
  offset: number;
  has_more: boolean;
}

export interface WorkflowRow {
  job_id: string;
  job_name: string;
  analyst_id: string;
  author_id: string;
  job_created_at: string;
  is_live: boolean;
  run_id: string;
  run_created_at: string;
  run_status: string;
}

export interface WorkflowsResponse {
  rows: WorkflowRow[];
  total: number;
  offset: number;
  has_more: boolean;
}

export interface AnalystRow {
  analyst_id: string;
  analyst_name: string;
  author_id: string;
  created_at: string;
  last_modified_at: string;
}

export interface AnalystsResponse {
  rows: AnalystRow[];
  total: number;
  offset: number;
  has_more: boolean;
}

export interface MemoryRow {
  memory_id: string;
  analyst_id: string;
  source: string;
  enabled: boolean;
  added_by: string;
  created_at: string;
  session_id: string;
}

export interface MemoryResponse {
  rows: MemoryRow[];
  total: number;
  offset: number;
  has_more: boolean;
}

/**
 * Discriminant shared by both timeline types below.
 * Use the const values (TimelineKind.REASONING, TimelineKind.RUN) instead of raw strings.
 * Add new kinds here — TimelineEntry and TimelineItem both derive from this.
 */
export const TimelineKind = {
  REASONING: "reasoning",
  RUN: "run",
} as const;

/**
 * One entry in the unified rendering timeline for an AI turn.
 * Stored in ChatMessage.timelineOrder and passed as props — acts as an index
 * pointer into streamingReasoningGroups / ToolExecutionRun arrays.
 */
export type TimelineEntry = { kind: (typeof TimelineKind)[keyof typeof TimelineKind]; idx: number };

/**
 * Resolved/hydrated timeline item used only at render time inside ExecutionStepsCard.
 * Built by looking up TimelineEntry pointers into the actual data arrays.
 */
export type TimelineItem =
  | { kind: typeof TimelineKind.REASONING; group: ReasoningGroup }
  | { kind: typeof TimelineKind.RUN; run: ToolExecutionRun; runIndex: number };

/**
 * A single streaming reasoning group produced by the LLM during a turn.
 * Tokens with the same `index` belong to the same thought block.
 */
export interface ReasoningGroup {
  /** Reasoning index from the StreamResponseProto — groups tokens belonging to the same thought block. */
  index: number;
  /** Accumulated reasoning text for this block. */
  text: string;
  /** True while this block is the active streaming group; collapses when the index changes or streaming ends. */
  isOpen: boolean;
  /** Wall-clock ms when the first token of this group arrived (used to compute duration). */
  startTimeMs: number;
  /** Duration in ms from first token to group close. Undefined while the group is still open. */
  durationMs?: number;
}

/**
 * A single message turn in a chat session (user or AI).
 * Shared between Chat, MessageComponent, MarkdownRenderer, and related containers.
 */
export interface ChatMessage {
  id?: string;
  author: "user" | "ai";
  content: string;
  artifacts?: ArtifactProto[] | undefined;
  responseMessage?: ResponseMessageProto | undefined;
  selectedAnalystId?: string;
  ls_trace_url?: string;
  langgraph_message_json_list?: string[];
  userTimestampMs?: number;
  executionStatus?: ExecutionStatusType;
  executionSteps?: ExecutionStepProto[];
  /**
   * End timestamp (ms) of the message immediately before this AI turn.
   * = last execution step's timestamp_ms of the previous message (if it has steps)
   *   OR that previous message's own timestamp_ms.
   * Used to compute the first reasoning block's duration on reload.
   */
  prevMessageEndTimestampMs?: number;
  /** Reasoning thought blocks streamed from the LLM before/between tool calls. */
  streamingReasoningGroups?: ReasoningGroup[];
  /**
   * Unified timeline order for the streaming case — appended in stream arrival order.
   *   kind "reasoning" → streamingReasoningGroups[idx]
   *   kind "run"       → i-th ToolExecutionRun derived from executionSteps
   */
  timelineOrder?: TimelineEntry[];
  /** ArtifactProtos for files uploaded with this human message — rendered as chips above the message. */
  fileArtifacts?: ArtifactProto[];
}

/**
 * Options passed alongside an artifact when requesting it to be opened.
 * Named fields replace positional args so call sites are self-documenting.
 */
export type ArtifactOpenOpts = {
  /** Tab to activate: "data" for dataframes, "charts" for charts, "code" for the code view */
  activeTab?: string;
  // Name of the sub-resource (pandas_df or chart) being opened.
  //  The index is always resolved from this name inside openDataFrameAnalysis — both when the
  //  artifact is already hydrated and after populate() returns — so a pre-computed index is
  //  never needed at call sites.
  subResourceName?: string;
  // ArtifactKind of the artifact being opened — set by getArtifactOpenOpts so that
  //  lookupByName can do a precise (type, name) lookup instead of searching all buckets.
  //  Typed as string to avoid a circular dep with artifactUtils (ArtifactKind is a string union).
  kind?: string;
};

/**
 * A single artifact that has been opened in the artifact panel.
 * Each item maps to one tab (or contributes to a grouped ANALYSIS_OUTPUT tab).
 */
export type SelectedArtifactItem = {
  artifact?: ArtifactProto;
  kgDataFrameData?: KGDataFrameProto;
  langgraphMessage?: string[];
  orderIndex: number;
  loading: boolean;
  analysisGroupId?: string;
  artifactView?: {
    activeTab?: string;
    /** Name of the selected primary pill (result/output — dataframe, chart, report, or workflow run output) */
    selectedPillName?: string;
    /** Name of the selected input pill (parent_header.name in the Inputs tab) */
    selectedInputPillName?: string;
  };
};
